import { IConfidence, IReminderSchedule, IGoal, IOkrClosedStatus } from "@gtmhub/goals/models";
import { IUnitFormatting } from "@gtmhub/shared/models";
import { IPlanningSession } from "@gtmhub/sessions/models/models";
import { CfMap } from "@gtmhub/customFields";
import { IGif, INewComment } from "@gtmhub/comments";

// to do: when this IMetric model is refactored IAlignmentTreeMetric can be refactored as well to extend metric base, extract props from other etc.
export interface IMetric {
  id?: string;
  accountId?: string;
  name?: string;
  goalId?: string;
  goalName?: string;
  sessionId?: string;
  insightName?: string;
  fieldName?: string;
  settings?: IMetricSettings[];
  targetOperator?: MetricTargetOperator;
  target?: number;
  critical?: number;
  initialValue?: number;
  actual?: number;
  attainment?: number;
  progressStatus?: string;
  trend?: IMetricTrend[];
  manualType?: string;
  softDueDate?: string;
  dueDate?: string;
  reminderSchedule?: IReminderSchedule;
  description?: string;
  goalClosedStatus?: Pick<IOkrClosedStatus, "status">;
  customFields?: CfMap;
  lastCheckInDate?: string;
  metricHistory?: IMetricSnapshot[];
  confidence?: IConfidence;
  format?: IUnitFormatting;
  links?: IMetricLinks;
  obfuscated?: boolean;
  cascadeType?: MetricCascadeType;
  // below are properties only used in the UI
  color?: string;
  deleting?: boolean;
  percentTime?: number; // used in the webapp for the timestamped KRs
  daysLeft?: number; // used in the webapp for the timestamped KRs
  percentTimeToSoftDeadline?: number;
  daysLeftToSoftDeadline?: number;
  draft?: boolean;
  // remove ownerId from model
  ownerId?: string;
  ownerIds?: string[];
  sourceMetricId?: string;
  targetMetrics?: string[];
  dynamic?: boolean;
  // Used for drag & drop list in single goal modal
  childType?: "metric";
  orderId?: number;
}

export type MetricCascadeType = "absolute_value" | "delta_value" | "";
export type MetricTargetOperator = "at_least" | "at_most" | "should_stay_above" | "should_stay_below" | "boolean";
export type MetricLinksSpec = Pick<IMetricLinks, "spec">;
export interface IPatchMetricLinks {
  linksToCreate?: MetricLinksSpec;
  linksToDelete?: MetricLinksSpec;
}

export interface IMetricLinks {
  expanded?: IOkrLinksExpanded;
  spec: IMetricLinksSpec;
}

export interface IOkrLinksExpanded {
  sessions: IPlanningSession[];
  goals: IGoal[];
  metrics: IMetric[];
}

export interface IMetricLinksSpec {
  contributing?: IMetricLinksSpecDetails[];
  cascading?: IMetricLinksSpecDetails[];
}

export interface IMetricLinksSpecDetails {
  id: string;
  type: "metric";
}

export interface IMetricSettings {
  name: string;
  title?: string;
  value: string;
  valueDisplayName?: string;
  type?: string;
}

export interface IMetricSnapshot {
  id: string;
  updatedAt: string;
  createdAt: string;
  metricValue: number;
  confidenceValue: number;
  changedBy: string;
  comment: string;
  valueChange: number;
  attainmentChange: number;
  currentAttainment: number;
  commentsCount: number;
  sourceInfo?: ISnapshotSourceInfo;
  // below properties are used in the UI
  updatedLate?: boolean;
  view?: {
    color: string;
  };
}

export interface IMetricTrend {
  date: Date;
  projected: number;
  actual: number;
}

export interface ISnapshotSourceInfo {
  id: string;
  name: string;
  ownerIds: string[];
  updateReason: "valueChange" | "contributorAdded" | "contributorDeleted";
}

export interface ICheckin {
  id?: string;
  actual?: number;
  checkInDate?: string;
  createdAt?: string;
  confidence?: number;
  comment?: string;
  changedBy?: string;
  customFields?: CfMap;
  simulateAutomaticCheckin?: boolean;
  gif?: IGif;
}

export interface ICheckinV2 {
  format: IUnitFormatting;
  accountId: string;
  attainment: number;
  attainmentChange: number;
  comment: string;
  confidenceValue: number;
  createdAt: string;
  date: string;
  goalId: string;
  id: string;
  metricId: string;
  modifiedBy: string;
  modifiedByInfo: {
    accountId: string;
    avatar: string;
    color: string;
    email: string;
    id: string;
    name: string;
    picture: string;
    type: string;
  };
  name: string;
  ownerId: string;
  target: number;
  targetOperator: string;
  manualType: string;
  value: number;
  gif?: IGif;
  newComment?: INewComment;

  // ux
  showForm: boolean;
}

export interface IPartialMetric {
  id: string;
  name?: string;
  insightName?: string;
  fieldName?: string;
  targetOperator?: MetricTargetOperator;
  target?: number;
  critical?: number;
  initialValue?: number;
  actual?: number;
  manualType?: string;
  softDueDate?: string;
  dueDate?: string;
  description?: string;
  ownerId?: string;
  customFields?: CfMap;
  format?: IUnitFormatting;
  cascadeType?: MetricCascadeType;
  attainment?: number;
}

export enum MetricFormEventType {
  KR_FORM_OPENED = "KR Form Opened",
  KR_FORM_CLOSED = "KR Form Closed",
}

export enum MetricType {
  DYNAMIC = "dynamic",
  MANUAL = "manual",
}

export enum MetricFormState {
  EDIT = "edit",
  CREATE = "create",
}

export interface IMetricsQueryParamsOptions {
  goalIds?: string;
  ids?: string;
}

export type CascadingLinkType = "cascading";
